import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import featureFlagService from './services/featureFlagService.ts';
import { post } from './utils/ApiClient';
import { getUser } from './resources/api.resource.ts';
import AppRoutes from './components/routes/AppRoutes';
import logger from './utils/logger';
import { useSessionSync, broadcastLogin } from './hooks/useSessionSync';
import useInactivityTimer from './hooks/useInactivityTimer';
import SessionExpiredModal from './components/sharedComponents/SessionExpiredModal';
import { SESSION_EXPIRED_EVENT } from './resources/api.resource.ts';

const App = () => (
  <Router>
    <AppContent />
  </Router>
);

const AppContent = () => {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const isAuthenticated = localStorage.getItem('isAuthenticated') && accounts.length > 0;
  // T1 uses cookie auth; MSAL accounts may be empty. Fall back to backend identity.
  const msalMail = isAuthenticated ? accounts[0]?.username || accounts[0]?.name : '';
  const msalName = isAuthenticated ? accounts[0]?.name || '' : '';
  const [logedInUserMail, setLogedInUserMail] = useState(msalMail);
  const [logedInUserName, setLogedInUserName] = useState(msalName);
  const [username, setUsername] = useState('');
  const [sessionExpiredModalOpen, setSessionExpiredModalOpen] = useState(false);

  const sessionCookie = document.cookie.split('; ').find((row) => row.startsWith('session_exists='))?.split('=')[1] ?? '';
  const hasActiveSession = sessionCookie.toLowerCase() === 'true' || sessionCookie === '1';

  useSessionSync();
  useInactivityTimer(hasActiveSession);

  // Cookie-auth path: MSAL has no accounts, so get identity from the T1 backend.
  useEffect(() => {
    if (msalMail) return;
    getUser()
      .then((user) => {
        if (user?.email) setLogedInUserMail(user.email);
        if (user?.name) setLogedInUserName(user.name);
      })
      .catch(() => {});
  }, [msalMail]);

  // Listen for session expired event
  useEffect(() => {
    const handleSessionExpired = () => {
      setSessionExpiredModalOpen(true);
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);

    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    };
  }, []);

  useEffect(() => {
    const handleRedirect = async () => {
      const response = await instance.handleRedirectPromise();
      if (response?.account) {
        instance.setActiveAccount(response.account);
        const userMail = response.account?.username;
        const responseBody = await post(
          'issue-token',
          { username: userMail },
          { Authorization: `Bearer ${response?.accessToken}` },
        );

        if (responseBody?.message) {
          localStorage.setItem('isAuthenticated', true);
          toast.dismiss();
          broadcastLogin();
          navigate('/landing');
        } else {
          logger.error('Token not found in response.');
        }
      }
    };

    //handleRedirect();
  }, [instance]);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        await featureFlagService.initialize();
      } catch (error) {
        console.error('Failed to initialize app:', error);
      }
    };

    initializeApp();

    // Cleanup on unmount
    return () => {
      featureFlagService.destroy();
    };
  }, []);

  const handleLogin = (userEmail) => {
    const loginRequest = {
      scopes: ['User.Read'],
      loginHint: userEmail,
    };

    instance.loginRedirect(loginRequest).catch((error) => {
      logger.error('Login failed:', error);
    });
  };

  return (
    <div>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss={false}
      />
      <SessionExpiredModal open={sessionExpiredModalOpen} />
      <AppRoutes
        isAuthenticated={isAuthenticated}
        handleLogin={handleLogin}
        setUsername={setUsername}
        logedInUserMail={logedInUserMail}
        logedInUserName={logedInUserName}
        username={username}
      />
    </div>
  );
};

export default App;
